<template>
  <div class="container mx-auto px-4 py-6 space-y-6">
    <!-- Form Content -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 p-4 rounded-t-lg">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-xl font-bold text-white">
              {{ $t('Mobile.MobilePlantRecord.title') }}
            </h2>

            <p class="text-sm text-white/90 mt-1">
              {{ plant?.fleet }}
            </p>

            <p class="text-sm text-white/90">
              {{ plant?.description }}
            </p>
          </div>

          <div class="text-right">
            <div class="text-sm font-medium text-white/90">
              {{ day.toLocaleDateString() }}
            </div>
          </div>
        </div>
      </div>

      <form
        class="p-4 space-y-6"
        @submit.prevent="saveRecord"
      >
        <!-- Time Details Section -->
        <div class="space-y-4">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ $t('Mobile.MobilePlantRecord.timeDetails') }}
          </h3>

          <!-- Night Shift Toggle -->
          <div
            v-if="!record"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <span class="text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.nightShift') }}
            </span>

            <label class="relative items-center cursor-pointer">
              <input
                v-model="toggleNightShift"
                type="checkbox"
                class="peer sr-only"
              >

              <div class="peer btn--switch-blue peer-checked:bg-ccm-blue-100 w-9 h-5 after:w-4 after:h-4" />
            </label>
          </div>

          <template v-if="!plant?.smu_tracking">
            <!-- Start Time -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('Mobile.MobilePlantRecord.startTime') }}
              </label>

              <AppInput
                v-model="recordForm.form.start_at"
                class="form-input"
                name="start_at"
                type="time"
                data-test="start_at"
                :error="recordForm.getErrors('start_at')"
              />
            </div>

            <!-- End Time -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('Mobile.MobilePlantRecord.endTime') }}
              </label>

              <AppInput
                v-model="recordForm.form.end_at"
                class="form-input"
                name="end_at"
                :type="endAtType"
                data-test="end_at"
                :error="recordForm.getErrors('end_at')"
              />
            </div>
          </template>

          <template v-else>
            <!-- SMU Start -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('Mobile.MobilePlantRecord.smuStart') }}
              </label>

              <AppInput
                v-model.number="recordForm.form.smu_start"
                name="smu_start"
                class="form-input"
                type="number"
                data-test="smu_start"
                :error="recordForm.getErrors('smu_start')"
              />
            </div>

            <!-- SMU for record -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('Mobile.MobilePlantRecord.smuForRecord') }}
              </label>

              <AppInput
                v-model.number="recordForm.form.smu"
                name="smu"
                class="form-input"
                type="number"
                data-test="smu"
                :error="recordForm.getErrors('smu_end')"
              />
            </div>
          </template>

          <!-- Total Time/SMU End Display -->
          <div
            v-if="plant?.smu_tracking"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <span class="text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.smuEnd') }}
            </span>

            <span class="text-sm font-medium text-gray-900">
              {{ recordForm.form.smu_end }}
            </span>
          </div>

          <div
            v-else
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <span class="text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.totalTime') }}
            </span>

            <span class="text-sm font-medium text-gray-900">
              {{ totalTime > 0 ? `${totalTime.toFixed(1)} hrs` : '' }}
            </span>
          </div>

          <!-- Breakdown -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.breakdown') }}
            </label>

            <AppSelect
              v-model.number="recordForm.form.breakdown"
              :options="breakdownOptions"
              class="form-input"
              name="breakdown"
              data-test="breakdown"
              :error="recordForm.getErrors('breakdown')"
            />
          </div>

          <!-- Standdown -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.standdown') }}
            </label>

            <AppSelect
              v-model.number="recordForm.form.standdown"
              :options="standdownOptions"
              class="form-input"
              name="standdown"
              data-test="standdown"
              :error="recordForm.getErrors('standdown')"
            />
          </div>

          <!-- Operating Time Display -->
          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span class="text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.operating') }}
            </span>

            <span class="text-sm font-medium text-gray-900">
              {{ (Math.max(totalTime - recordForm.form.breakdown - recordForm.form.standdown, 0)).toFixed(1) }} hrs
            </span>
          </div>
        </div>

        <!-- Location Details Section -->
        <div class="space-y-4">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ $t('Mobile.MobilePlantRecord.locationDetails') }}
          </h3>

          <!-- Area -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.area') }}
            </label>

            <AppSelect
              v-model="recordForm.form.area"
              :options="project?.areas"
              class="form-input"
              name="area"
              data-test="area"
              :error="recordForm.getErrors('area')"
            />
          </div>

          <!-- Element -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.element') }}
            </label>

            <AppSelect
              v-model="recordForm.form.element"
              :options="project?.elements"
              class="form-input"
              name="element"
              data-test="element"
              :error="recordForm.getErrors('element')"
            />
          </div>

          <!-- Activity -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.activity') }}
            </label>

            <AppSelect
              v-model="recordForm.form.activity"
              :options="project?.activities"
              class="form-input"
              name="activity"
              data-test="activity"
              :error="recordForm.getErrors('activity')"
            />
          </div>

          <!-- WBS Code -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.wbsCode') }}
            </label>

            <AppSelect
              v-model.number="recordForm.form.wbs_code_id"
              :options="wbsCodes.map((code) => ({ value: code.id, label: code.code }))"
              class="form-input"
              name="wbs_code"
              data-test="wbs_code"
              :error="recordForm.getErrors('wbs_code_id')"
            />
          </div>

          <!-- WBS Description -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.wbsDescription') }}
            </label>

            <AppSelect
              v-model.number="recordForm.form.wbs_code_id"
              class="form-input"
              :options="wbsCodes.map((code) => ({ value: code.id, label: code.description }))"
              name="wbs_description"
              data-test="wbs_description"
            />
          </div>

          <!-- Off-hire -->
          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div class="flex items-center space-x-3">
              <input
                v-model="recordForm.form.off_hire"
                type="checkbox"
                name="off_hire"
                data-test="off_hire"
                class="w-4 h-4 text-ccm-blue-100 border-gray-300 rounded focus:ring-ccm-blue-100"
                @change="checkOffhire"
              >

              <span class="text-sm font-medium text-gray-700">{{ $t('Mobile.MobilePlantRecord.offHire') }}</span>
            </div>
          </div>
        </div>

        <!-- Variation/Delay Details Section -->
        <div
          v-if="variationOrders.length"
          class="space-y-4"
        >
          <h3 class="text-lg font-semibold text-gray-900">
            {{ $t('Mobile.MobilePlantRecord.variationDelayDetails') }}
          </h3>

          <!-- Event Number -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.eventNumber') }}
            </label>

            <div class="flex space-x-2">
              <div class="flex-1">
                <AppSelect
                  v-model.number="recordForm.form.variation_order_id"
                  :options="variationOrders.map((order) => ({ value: order.id, label: order.code }))"
                  name="vo_number"
                  data-test="vo_number"
                  :error="recordForm.getErrors('variation_order_id')"
                  class="form-input"
                  optional
                />
              </div>

              <button
                v-if="recordForm.form.variation_order_id"
                class="btn-icon btn-icon-red btn-icon--bg-red p-2"
                type="button"
                @click.prevent="recordForm.resetData(['variation_order_id'])"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
              </button>
            </div>
          </div>

          <!-- Event Description -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobilePlantRecord.eventDescription') }}
            </label>

            <AppSelect
              v-model.number="recordForm.form.variation_order_id"
              class="form-input"
              :options="variationOrders.map((order) => ({ value: order.id, label: order.description }))"
              name="vo_description"
              data-test="vo_description"
              :error="recordForm.getErrors('variation_order_id')"
              optional
            />
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex space-x-4 pt-6">
          <button
            v-if="!record?.approved_at && recordForm.form.off_hire"
            type="button"
            class="btn btn--tertiary-blue flex-1"
            @click.prevent="clearData"
          >
            {{ $t('Mobile.MobilePlantRecord.clear') }}
          </button>

          <button
            type="button"
            class="btn btn--secondary-blue flex-1"
            @click.prevent="router.push({ name: 'MobilePlantDashboard', query: { day: dateAsYMD(day) } })"
          >
            {{ $t('Mobile.MobilePlantRecord.cancel') }}
          </button>

          <p
            v-if="record?.approved_at"
            class="text-center text-sm text-gray-600 flex-1 flex items-center justify-center"
          >
            {{ $t('Mobile.MobilePlantRecord.recordApprovedCannotModify') }}
          </p>

          <button
            v-else-if="projectsStore.project?.unarchived"
            type="submit"
            class="btn btn--primary-blue flex-1"
            data-test="submit_form"
          >
            {{ record ? $t('Mobile.MobilePlantRecord.update') : $t('Mobile.MobilePlantRecord.save') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateAsYMD, dateForInput, hoursToDate, timeForInput, timeZone } from '@/helpers';
import { t } from '@/i18n';
import Plant from '@/models/Plant';
import Project from '@/models/Project';
import { VariationOrderStatus } from '@/models/VariationOrder';
import { useNavStore } from '@/store/nav';
import { usePlantDailyRecordsStore } from '@/store/plantDailyRecord';
import { usePlantsStore } from '@/store/plants';
import { useProjectsStore } from '@/store/projects';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { useWbsCodesStore } from '@/store/wbsCodes';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
  plantId: string;
  recordId?: string;
}>();

const projectsStore = useProjectsStore();
const wbsCodesStore = useWbsCodesStore();
const variationOrdersStore = useVariationOrdersStore();
const fetchApprovedVariations = useStoreApiAction(variationOrdersStore.fetchApprovedVariationOrders);

const wbsCodes = computed(() => {
  return wbsCodesStore.models.where('project_id', parseInt(props.projectId)).get();
});

const project = computed(() => {
  return projectsStore.project;
});

const plantsStore = usePlantsStore();

plantsStore.fetchPlant(props.plantId);
wbsCodesStore.index(props.projectId);
fetchApprovedVariations.request(props.projectId);

const plant = computed(() => {
  return plantsStore.models.query().with('records', (query) => {
    query.whereId(parseInt(props.recordId));
  }).find(parseInt(props.plantId));
});

const queryParams = useRoute().query.day as string;
const day = ref(queryParams ? new Date(queryParams) : new Date());
const navStore = useNavStore();

navStore.backRoute = { name: 'MobilePlantDashboard', query: { day: dateAsYMD(day.value) } };

const record = computed(() => {
  return plant.value?.records.length ? plant.value?.records[0] : undefined;
});

const recordForm = useFormData({
  day: dateAsYMD(day.value),
  start_at: '',
  end_at: '',
  smu_start: 0,
  smu_end: 0,
  smu: 0,
  breakdown: 0,
  standdown: 0,
  quantity: 0,
  area: '',
  element: '',
  activity: '',
  wbs_code_id: undefined,
  off_hire: false,
  comment: '',
  variation_order_id: undefined,
  time_zone: timeZone,
});

const endAtType = ref<'time' | 'datetime-local'>('time');

watch(endAtType, () => {
  recordForm.form.end_at = '';
});

watch(() => recordForm.form.start_at, (newValue, oldValue) => {
  if(newValue) {
    const hourSplit = newValue.split(':');
    const hour = parseInt(hourSplit[0]);

    if(hour >= 17) {
      endAtType.value = 'datetime-local';

      nextTick(() => {
        if(!oldValue && !recordForm.form.end_at) {
          const endAt = new Date(day.value);
          const hourSplit = newValue.split(':');

          endAt.setHours(
            !props.recordId ? parseInt(hourSplit[0]) + project.value.plant_standard_shift : parseInt(hourSplit[0]) + 2,
          );

          endAt.setMinutes(parseInt(hourSplit[1]));
          recordForm.form.end_at = dateForInput(endAt);
        }
      });
    } else {
      endAtType.value = 'time';

      nextTick(() => {
        if(!oldValue && !recordForm.form.end_at) {
          const endAt = new Date(day.value);
          const hourSplit = newValue.split(':');

          endAt.setHours(
            !props.recordId ? parseInt(hourSplit[0]) + project.value.plant_standard_shift : parseInt(hourSplit[0]) + 2,
          );

          endAt.setMinutes(parseInt(hourSplit[1]));
          recordForm.form.end_at = timeForInput(endAt);
        }
      });
    }
  }
});

const futureRecord = ref(false);

watch(() => recordForm.form.day, (newValue) => {
  if(newValue) {
    const dayAsDate = new Date(newValue);

    if(!isNaN(dayAsDate.getTime()) && dayAsDate.getTime() > new Date().getTime()) {
      futureRecord.value = true;
    } else {
      futureRecord.value = false;
    }
  }
}, { immediate: true });

watch(() => recordForm.form.smu, (newValue) => {
  if(newValue) {
    recordForm.form.smu_end = newValue + recordForm.form.smu_start;
  } else {
    recordForm.form.smu_end = recordForm.form.smu_start;
  }
}, { immediate: true });

const totalTime = computed(() => {
  if(recordForm.form.start_at && recordForm.form.end_at) {
    let endAt: Date = undefined;

    if(endAtType.value === 'time') {
      endAt = new Date(`${recordForm.form.day} ${recordForm.form.end_at}`);
    } else {
      endAt = new Date(recordForm.form.end_at);
    }

    const timeDiff = Math.abs(
      new Date(`${recordForm.form.day} ${recordForm.form.start_at}`).getTime() -
        endAt.getTime(),
    ) / 3600000;

    return Math.round(timeDiff * 2) / 2;
  }

  if(recordForm.form.smu_start && recordForm.form.smu_end) {
    const timeDiff = Math.abs(recordForm.form.smu_start - recordForm.form.smu_end);

    return Math.round(timeDiff * 2) / 2;
  }

  return 0;
});

watch(record, (newValue) => {
  if(newValue) {
    if(!plant.value?.smu_tracking) {
      recordForm.form.start_at = timeForInput(newValue.startAt);

      if(newValue.startAt.hour >= 17) {
        endAtType.value = 'datetime-local';

        nextTick(() => {
          recordForm.form.end_at = dateForInput(newValue.endAt);
        });
      } else {
        endAtType.value = 'time';

        nextTick(() => {
          recordForm.form.end_at = timeForInput(newValue.endAt);
        });
      }
    }

    recordForm.setData(newValue, [
      'start_at',
      'end_at',
      'time_zone',
    ]);

    if(plant.value.smu_tracking) {
      recordForm.form.smu = recordForm.form.smu_end - recordForm.form.smu_start;
    }

    day.value = new Date(newValue.day);
    navStore.backRoute = { name: 'MobilePlantDashboard', query: { day: dateAsYMD(day.value) } };
  }
});

const plantDailyRecordsStore = usePlantDailyRecordsStore();
const createRecordAction = useStoreApiAction(plantDailyRecordsStore.createRecord);
const updateRecordAction = useStoreApiAction(plantDailyRecordsStore.updateRecord);
const router = useRouter();

const saveRecord = () => {
  recordForm.resetErrors();

  if(record.value) {
    Swal.fire({
      text: t('Mobile.MobilePlantRecord.whyMakingChange'),
      icon: 'info',
      input: 'text',
      showConfirmButton: true,
      showCancelButton: true,
    }).then((result) => {
      if(result.isConfirmed) {
        recordForm.form.comment = result.value;

        updateRecordAction.request(record.value.id, recordForm.form, { day: recordForm.form.day }).then(() => {
          router.push({ name: 'MobilePlantDashboard', query: { day: dateAsYMD(day.value) } });
        }).catch((error) => {
          recordForm.setErrors(error.data);
        });
      }
    });
  } else {
    if(futureRecord.value) {
      Swal.fire({
        icon: 'warning',
        title: t('Mobile.MobilePlantRecord.futureDateRecord'),
        text: t('Mobile.MobilePlantRecord.saveRecordConfirmation'),
        showCancelButton: true,
      }).then((result) => {
        if(result.isConfirmed) {
          createRecordAction.request(plant.value?.id, recordForm.form).then(() => {
            router.push({ name: 'MobilePlantDashboard', query: { day: dateAsYMD(day.value) } });
          }).catch((error) => {
            recordForm.setErrors(error.data);
          });
        }
      });
    } else {
      createRecordAction.request(plant.value?.id, recordForm.form).then(() => {
        router.push({ name: 'MobilePlantDashboard', query: { day: dateAsYMD(day.value) } });
      }).catch((error) => {
        recordForm.setErrors(error.data);
      });
    }
  }
};

const previousRecord = computed(() => {
  return plantDailyRecordsStore.models
    .where('day', dateAsYMD(new Date(recordForm.form.day)))
    .where('plant_id', parseInt(props.plantId))
    .orderBy('id', 'desc')
    .first() ||
    plantDailyRecordsStore.models
      .where('plant_id', parseInt(props.plantId))
      .orderBy('id', 'desc')
      .first();
});

watch(previousRecord, (newValue) => {
  if(newValue && !props.recordId) {
    recordForm.form.area = newValue.area;
    recordForm.form.element = newValue.element;
    recordForm.form.wbs_code_id = newValue.wbs_code_id;
    recordForm.form.activity = newValue.activity;

    if(plant.value?.smu_tracking) {
      recordForm.form.smu_start = newValue.smu_end;
      recordForm.form.smu_end = newValue.smu_end + projectsStore.project?.plant_standard_shift;
      recordForm.form.smu = projectsStore.project?.plant_standard_shift;
    } else if(newValue.day === dateAsYMD(day.value) && !plant.value?.smu_tracking) {
      recordForm.form.start_at = timeForInput(newValue.end_at);
    }
  }
});

watch([() => projectsStore.project, () => plant], ([newProject, newPlant]) => {
  const previousRecord = newPlant ?
    plantDailyRecordsStore.models
      .where('day', dateAsYMD(day.value))
      .where('plant_id', plant.value?.id)
      .first() :
    undefined;

  if(
    !previousRecord &&
    !plant.value?.smu_tracking &&
    newProject
  ) {
    recordForm.form.start_at = newProject.shift_start_at;

    const end = new Date(newProject.shiftStartAt);

    end.setHours(end.getHours() + newProject.plant_standard_shift);

    if(hoursToDate(newProject.shift_start_at).getHours() >= 17) {
      recordForm.form.end_at = dateForInput(end);
    } else {
      recordForm.form.end_at = timeForInput(end);
    }
  }
}, { immediate: true });

const checkOffhire = (event: Event) => {
  if((event.target as HTMLInputElement).checked) {
    Swal.fire({
      icon: 'question',
      text: t('Mobile.MobilePlantRecord.removeDataQuestion'),
      showConfirmButton: true,
      confirmButtonColor: 'green',
      confirmButtonText: t('Mobile.MobilePlantRecord.yes'),
      showDenyButton: true,
    }).then((result) => {
      if(result.value) {
        recordForm.resetData([
          'start_at',
          'end_at',
          'smu_start',
          'smu_end',
          'breakdown',
          'standdown',
          'quantity',
          'area',
          'element',
          'activity',
          'wbs_code_id',
        ]);
      }
    });
  }
};

const clearData = () => {
  recordForm.resetData([
    'start_at',
    'end_at',
    'smu_start',
    'smu_end',
    'breakdown',
    'standdown',
    'quantity',
    'area',
    'element',
    'activity',
    'wbs_code_id',
  ]);
};

const standdownOptions = computed(() => {
  const maxTime = project.value?.maximum_shift - totalTime.value;
  const options = [];

  for(let i = 0;i <= maxTime;i += 0.5) {
    options.push(i);
  }

  return options;
});

const breakdownOptions = computed(() => {
  const maxTime = project.value?.maximum_shift - totalTime.value;
  const options = [];

  for(let i = 0;i <= maxTime;i += 0.5) {
    options.push(i);
  }

  return options;
});

const toggleNightShift = ref(false);

watch(toggleNightShift, (newValue) => {
  if(project.value) {
    const shiftStart = newValue ? project.value.night_shift_start_at : project.value.shift_start_at;
    const end = new Date(newValue ? project.value.nightShiftStartAt : project.value.shiftStartAt);

    end.setHours(end.getHours() + project.value.labour_standard_shift);
    recordForm.form.start_at = newValue ? project.value.night_shift_start_at : project.value.shift_start_at;

    nextTick(() => {
      if(hoursToDate(shiftStart).getHours() >= 17) {
        recordForm.form.end_at = dateForInput(end);
      } else {
        recordForm.form.end_at = timeForInput(end);
      }
    });
  }
});

const variationOrders = computed(() => {
  return variationOrdersStore.models.where('project_id', project.value?.id).where(
    'status',
    VariationOrderStatus.approved,
  ).get();
});
</script>

<style scoped></style>
